import { console } from './utility/console';
import { SESSION_INHERITANCE_EXPIRATION_LIMIT } from './constants';
import { Tracker } from './tracker.types';
import { validIdfvRegexp } from './idfv';
import { isValidBaseUrl } from './utility/location';

const MUTATED_HREF_CACHE = 'data-reproio-href-cache';
type MarkableElement = { [MUTATED_HREF_CACHE]: string } & HTMLAnchorElement;

export class CrossDomainHandler {
  private tracker: Tracker;
  public linkerDomains: string[];
  private mutationObserver: MutationObserver | null = null;

  public constructor(tracker: Tracker) {
    this.tracker = tracker;
    this.linkerDomains = [];
  }

  public get isActive(): boolean {
    return this.linkerDomains.length > 0;
  }

  public setLinkerDomains(domains: string[]): void {
    // 現在のドメインと同じドメインは指定されても無意味なので除外(むしろ余計な処理が発生して不具合につながる)
    // see https://app.clickup.com/t/6xty8m
    this.linkerDomains = domains.filter((domain) => !window.location.hostname.endsWith(domain));
  }

  public isLinkerDomain(hostname: Location['hostname']): boolean {
    return this.linkerDomains.some((linkerDomain) => {
      return linkerDomain.length && hostname.endsWith(linkerDomain);
    });
  }

  private isValidIdfv(idfv: string): boolean {
    if (!validIdfvRegexp.test(idfv)) {
      console.error('Aborting setup: Invalid idfv parameter value has been passed');
      return false;
    }
    return true;
  }

  private isValidEventTrackedAt(date: Date): boolean {
    if (isNaN(date.getTime())) {
      console.error('Aborting setup: Invalid lastEventTrackedAt parameter value has been passed');
      return false;
    }
    return true;
  }

  private isValidFirstSession(firstSessionValue: Number): boolean {
    if (firstSessionValue !== 1 && firstSessionValue !== 0) {
      console.error('Aborting setup: Invalid firstSessionValue parameter value has been passed');
      return false;
    }
    return true;
  }

  public verifySessionParametersFormat(paramValue: string | null): boolean {
    if (!paramValue) return false;

    const [idfv, lastEventTrackedAt, firstSessionValue] = paramValue.split(',');
    if (!(idfv && lastEventTrackedAt && firstSessionValue)) {
      console.error('Aborting setup: Missing session parameter(s)');
      return false;
    }

    return (
      this.isValidIdfv(idfv) &&
      this.isValidEventTrackedAt(new Date(parseInt(lastEventTrackedAt, 10))) &&
      this.isValidFirstSession(parseInt(firstSessionValue, 10))
    );
  }

  public verifySessionInheritability(paramValue: string | null): boolean {
    if (!paramValue) return false;

    const lastEventTrackedAt = paramValue.split(',')[1];
    const timeDiff = Date.now() - new Date(parseInt(lastEventTrackedAt, 10)).getTime();
    // Invalid if lastEventTrackAt was from over 1hr ago
    return timeDiff >= 0 && timeDiff <= SESSION_INHERITANCE_EXPIRATION_LIMIT;
  }

  public verifyLinkerDomains(domains: string[]): boolean {
    if (!Array.isArray(domains)) return false;

    return domains.every((domain) => {
      // Validationを緩めにする理由：https://github.com/reproio/web-sdk/pull/407#discussion_r301850812
      try {
        if (new URL(`http://${domain}`)) return true; // Fails if domain is empty
      } catch (err) {}
      return false;
    });
  }

  public addLinkerQueryParameterToAnchors = (anchors: NodeListOf<HTMLAnchorElement> | HTMLAnchorElement[]): void => {
    if (!isValidBaseUrl(window.location.origin)) return;

    // 古いブラウザでは Nodelist のような Array-Like なオブジェクトに対して直接 forEach できず、Array.prototype.forEach() を使って回避する
    // ref. https://developer.mozilla.org/ja/docs/Web/API/NodeList
    [].forEach.call(anchors, (anchor: HTMLAnchorElement) => {
      if (this.isLinkerDomain(anchor.hostname)) {
        try {
          const url = new URL(anchor.href, window.location.origin);
          // iOS 9 URL().searchParams がないため、polyfillが入ってる URLSearchParams を使う
          const searchParams = new URLSearchParams(url.search);
          searchParams.set('rpr', this.tracker._generateLinkerParameters());
          const paramString = `?${searchParams}`;

          // eslint-disable-next-line no-param-reassign
          anchor.href = `${url.origin}${url.pathname}${paramString}${url.hash}`;
          // eslint-disable-next-line no-param-reassign
          (anchor as MarkableElement)[MUTATED_HREF_CACHE] = anchor.href;
        } catch (err: any) {
          this.tracker._log('error', err);
        }
      }
    });
  };

  private handleElementsMutation(mutations: MutationRecord[]): void {
    mutations.forEach((mutation) => {
      // element.querySelectorAll を使って a タグの要素を書き換えたいが node には Node 型がついており不整合を起こすので、any 型で逃している
      [].forEach.call(mutation.addedNodes, (node: FIXME) => {
        if (node.tagName === 'A') return this.addLinkerQueryParameterToAnchors([node]);
        return node.querySelectorAll && this.addLinkerQueryParameterToAnchors(node.querySelectorAll('a'));
      });

      // In case of attributes modification.
      if (
        mutation.type === 'attributes' &&
        mutation.target.nodeName === 'A' &&
        mutation.attributeName === 'href' &&
        (mutation.target as MarkableElement)[MUTATED_HREF_CACHE] !== (mutation.target as HTMLAnchorElement).href
      ) {
        this.addLinkerQueryParameterToAnchors([mutation.target as HTMLAnchorElement]);
      }
    });
  }

  public addMutationObserver(isDisabled: boolean): void | false {
    // Prevent adding multiple observers
    if (isDisabled || this.mutationObserver) return false;

    this.mutationObserver = new MutationObserver((mutations: MutationRecord[]) =>
      this.handleElementsMutation(mutations)
    );
    this.mutationObserver.observe(document, { childList: true, subtree: true, attributes: true });
  }

  public reset() {
    if (!this.mutationObserver) {
      return;
    }
    this.mutationObserver.disconnect();
    this.mutationObserver = null;
  }
}
